<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceLineGroup',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    groupId: {
      type: String,
      required: false,
    },
    isDefaultGroup: {
      type: Boolean,
      required: false,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formId = ref(null);
  const formName = ref('invoiceLineGroup');
  const expenseTypes = ref(null);
  const titles = ref({
    incoming: $fn.value.pgettext('Invoice', 'Create Incoming Invoice'),
    outgoing: $fn.value.pgettext('Invoice', 'Create Outgoing Invoice'),
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_7307096920626688_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));
  const b_3119842801924129_dataSource = computed(() => ({
    name: 'suggest_property',
  }));
  const b_4994395583501828_dataSource = computed(() => ({
    name: 'suggest_worksheet',
  }));
  const b_2260153975480357_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
      contractors_only: true,
    },
  }));

  const $data = computed(() => ({
    item: item.value,
    formId: formId.value,
    formName: formName.value,
    expenseTypes: expenseTypes.value,
    titles: titles.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7307096920626688_dataSource: b_7307096920626688_dataSource.value,
    b_3119842801924129_dataSource: b_3119842801924129_dataSource.value,
    b_4994395583501828_dataSource: b_4994395583501828_dataSource.value,
    b_2260153975480357_dataSource: b_2260153975480357_dataSource.value,
  }));

  const $$load_item = () => {
    return props.groupId
      ? $fn.value.fetch('read_invoice_line_group', {
          asResource: true,
          parameters: { id: props.groupId },
        })
      : $fn.value.schemaDefaults('CreateInvoiceLineGroup', {
          invoice_id: props.invoice.id,
          is_default_group: props.isDefaultGroup,
        });
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_expenseTypes = () => {
    return $fn.value
      .fetch('list_object_by_type', {
        parameters: { object_type: 'expense-type' },
      })
      .then((d) => _.orderBy(d, ['name']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'expenseTypes' },
      { name: 'titles' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7307096920626688_dataSource', nodeId: '7307096920626688' },
      { name: 'b_3119842801924129_dataSource', nodeId: '3119842801924129' },
      { name: 'b_4994395583501828_dataSource', nodeId: '4994395583501828' },
      { name: 'b_2260153975480357_dataSource', nodeId: '2260153975480357' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      expenseTypes: $$load_expenseTypes,
    },
    ux: {},
    dataRefs: {
      item,
      formId,
      formName,
      expenseTypes,
      titles,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7307096920626688_dataSource,
      b_3119842801924129_dataSource,
      b_4994395583501828_dataSource,
      b_2260153975480357_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const incomingInvoice = computed(() => {
    return invoiceKind === 'incoming';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          item.value.isNew &&
            $fn.value.pgettext('InvoiceLineGroup', 'New line group'),
          !item.value.isNew &&
            $fn.value.pgettext('InvoiceLineGroup', 'Edit line group'),
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.groupId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('groupId');
    }
  );
  watch(
    () => props.isDefaultGroup,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('isDefaultGroup');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_e8iqjyr6v_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_yq39y8kg9_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_896wztpa9_getActionArgs = async ($event) => {
    return {
      objectType: 'invoice_line_group',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_2140198529698299_closeModal_0ernyhdwz_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: {
          deleted: true,
        },
      },
    };
  };
  const act_2167826738365259_closeModal_m6a7whwjl_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_e8iqjyr6v =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_e8iqjyr6v_getActionArgs,
    });
  const inst_evh_7315092382398562_close_n9drt3pk8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_e8iqjyr6v,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_n9drt3pk8],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_kp0m6fgzj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4ud01mq4g =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kp0m6fgzj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4ud01mq4g],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_3a6okca9e =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_j4rj152ka =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_3a6okca9e,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_j4rj152ka],
    event: 'close',
  });
  const act_1040167445267876_closeModal_yq39y8kg9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_yq39y8kg9_getActionArgs,
    });
  const inst_evh_1040167445267876_success_iu8fvwa01 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_yq39y8kg9,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_896wztpa9 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_896wztpa9_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_iu8fvwa01],
  });
  const inst_evh_1040167445267876_submit_sg2crshr8 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_896wztpa9,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_sg2crshr8],
    event: 'submit',
  });
  const act_2140198529698299_closeModal_0ernyhdwz =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_0ernyhdwz_getActionArgs,
    });
  const inst_evh_2140198529698299_success_6h5ovwy6t =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_0ernyhdwz,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_2140198529698299_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2140198529698299_success_6h5ovwy6t],
    event: 'success',
  });
  const act_2167826738365259_closeModal_m6a7whwjl =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_m6a7whwjl_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_qbvs2d9br =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_m6a7whwjl,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_qbvs2d9br],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_n9drt3pk8,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4ud01mq4g,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_j4rj152ka,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_iu8fvwa01,
    inst_evh_1040167445267876_submit_sg2crshr8,
    submit: inst_evh_1040167445267876_submit_sg2crshr8,
    inst_evh_1040167445267876_submit,
    inst_evh_2140198529698299_success_6h5ovwy6t,
    inst_evh_2140198529698299_success,
    inst_evh_2167826738365259_clickPreventStop_qbvs2d9br,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_n9drt3pk8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_n9drt3pk8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4ud01mq4g = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4ud01mq4g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_j4rj152ka = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_j4rj152ka.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_iu8fvwa01 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_iu8fvwa01.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_sg2crshr8 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_sg2crshr8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_6h5ovwy6t = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_6h5ovwy6t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_qbvs2d9br = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_qbvs2d9br.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceLineGroup"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        v-if="loaded"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="description"
            :label="pgettext('InvoiceLineGroup', 'Description')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormInput
              name="description"
              v-model="item.description"
              :readonly="item.readonly"
              :placeholder="
                pgettext(
                  'InvoiceLineGroup/description/placeholder',
                  'Enter group description'
                )
              "
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="expense_type"
            :label="pgettext('InvoiceLineGroup', 'Expense Type')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormSearchableSelect
              name="expense_type"
              v-model="item.expense_type"
              :readonly="item.readonly"
              clearable
              :options="expenseTypes"
              valueField="name"
              textField="name"
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="rental"
            :label="pgettext('InvoiceLineGroup', 'Rental')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormSearchableSelect
              name="rental"
              v-model="item.rental"
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_7307096920626688_dataSource"
              optionTemplate="<UcSuggestRentalOption :option=option>
</UcSuggestRentalOption>"
              use-option-template-for-selected-option
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="property"
            :label="pgettext('InvoiceLineGroup', 'Property')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormSearchableSelect
              name="property"
              v-model="item.property"
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_3119842801924129_dataSource"
              optionTemplate="<UcSuggestPropertyOption :option=option>
</UcSuggestPropertyOption>"
              use-option-template-for-selected-option
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="worksheet"
            :label="pgettext('InvoiceLineGroup', 'Worksheet')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormSearchableSelect
              name="worksheet"
              v-model="item.worksheet"
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_4994395583501828_dataSource"
              optionTemplate="<UcSuggestWorksheetOption :option=option>
</UcSuggestWorksheetOption>"
              use-option-template-for-selected-option
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
          <ItpFormGroup
            name="contractor"
            :label="pgettext('InvoiceLineGroup', 'Contractor')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
          >
            <ItpFormSearchableSelect
              name="contractor"
              v-model="item.contractor"
              bind-object
              :readonly="item.readonly"
              clearable
              :dataSource="b_2260153975480357_dataSource"
              optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
              use-option-template-for-selected-option
            >
            </ItpFormSearchableSelect>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <BtnDeleteResource
          :resource="item"
          variant="danger"
          size="md"
          class="me-auto"
          v-if="!item.isNew && !invoice.readonly && !item.readonly"
          @success="evh_2140198529698299_success($event, {})"
        >
        </BtnDeleteResource>
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          icon="fa-save"
          :text="gettext('Save')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
